import { Component, computed, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DEFAULT_PANEL_ROLES } from '@shared/auth/enums/app-role.enum';
import { NavigateService } from '@shared/services/navigate.service';
import {
  BasePanelChangeEvent,
  BasePanelChangeEventType,
  BasePanelService
} from '@shared/modules/base-panel/services/base-panel.service';
import { Subscription } from 'rxjs';
import { StorageService } from '@core/services/storage.service';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { TabLink } from '@shared/interfaces/menu/tab-link.interface';
import { HeaderConfig } from '@shared/configs/header.config';
import { Employee } from '@shared/models/employee.model';
import { RoleAccessService } from '@shared/auth/services/role-access.service';
import { ContactController } from '@modules/contacts/shared/controllers/contact.controller';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import { EventSidenavController } from '@shared/modules/event-sidenav/controllers/event-sidenav.controller';
import { MailingController } from '@shared/modules/mailings/shared/controllers/mailing.controller';
import { ChangesSidenavController } from '@shared/modules/task-sidenav/controllers/changes-sidenav.controller';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { HeaderLogoComponent } from './components/header-logo/header-logo.component';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { HeaderService } from './services/header.service';
import { HeaderHamburgerComponent } from './components/header-hamburger/header-hamburger.component';
import { UIModule } from '@shared/modules/ui/ui.module';
import { HeaderActionMenuComponent } from './components/header-action-menu/header-action-menu.component';
import { HeaderActionMenuUserComponent } from './components/header-action-menu/components/header-action-menu-user/header-action-menu-user.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasRoleDirective } from '@shared/auth/directives/has-role.directive';

@Component({
  selector: 'intilio-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderLogoComponent,
    SvgIconComponent,
    TranslateModule,
    MatMenuModule,
    HeaderHamburgerComponent,
    UIModule,
    HeaderActionMenuComponent,
    HeaderActionMenuUserComponent,
    MatTooltipModule,
    HasRoleDirective
  ],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('200ms', style({ opacity: 0 }))])
    ]),
    trigger('openClose', [
      state('true', style({ width: '230px' })),
      state('false', style({ width: '68px' })),
      transition('false <=> true', animate(200))
    ])
  ],
  providers: [HeaderService]
})
export class HeaderComponent implements OnInit, OnDestroy {
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  protected readonly defaultPanelRoles = DEFAULT_PANEL_ROLES;

  sub: Subscription;
  tabLinks: TabLink[] = [];

  @ViewChild('header') header: ElementRef;

  employee: Employee;

  protected isFullyExpanded = computed(
    () => this.basePanelService.isExpanded() || this.basePanelService.sideManuallyExpanded()
  );

  protected isCollapsed = computed(() => !this.isFullyExpanded());

  public readonly n = inject(NavigateService);
  protected readonly basePanelService = inject(BasePanelService);
  public readonly store = inject(StorageService);
  public readonly headerService = inject(HeaderService);
  private readonly roleAccessService = inject(RoleAccessService);

  constructor() {
    this.configureNavigation();

    this.employee = this.store.Employee;
  }

  ngOnInit(): void {
    this.listenChange();
  }

  handleHeaderMouseEnter(): void {
    this.basePanelService.handleHeaderMouseEnter();
  }

  listenChange() {
    this.basePanelService.panelChangeEmitter.subscribe((event: BasePanelChangeEvent) => {
      switch (event.type) {
        case BasePanelChangeEventType.HIDE_TOP_MENU:
          this.header.nativeElement.classList.add('hide');
          break;
        case BasePanelChangeEventType.SHOW_TOP_MENU:
          this.header.nativeElement.classList.remove('hide');
          break;
      }
    });
  }

  configureNavigation() {
    this.tabLinks = (Object.values(HeaderConfig) as TabLink[]).filter((tabLink) =>
      this.roleAccessService.hasAnyRole(tabLink.roles)
    );
  }

  ngOnDestroy() {
    this.sub ? this.sub.unsubscribe() : null;
  }

  createTask() {
    const ctrl = new TaskSidenavController();
    ctrl.addTask();
    this.headerService.closeMenu();
  }

  createChange() {
    const ctrl = new ChangesSidenavController();
    ctrl.addChange(null);
    this.headerService.closeMenu();
  }

  createEvent() {
    const ctrl = new EventSidenavController();
    ctrl.createEvent();
    this.headerService.closeMenu();
  }

  createContact() {
    const ctrl = new ContactController();
    ctrl.add();
  }

  createProjectDraft() {
    const ctrl = new ProjectController();
    ctrl.add();
    this.headerService.closeMenu();
  }

  createEmail() {
    const ctrl = new MailingController();
    ctrl.openMailingSelection('');
    this.headerService.closeMenu();
  }

  isTimelineRoute(tabLink: TabLink) {
    const isProjects = window.location.href.indexOf('projects') !== -1;
    if (tabLink.link === 'projects-list' && isProjects) return true;
    return false;
  }
}
